<template>
  <div>
    <input-rich-text
      data-testid="occurrences-content-editor"
      :elem-id="elemId"
      :show-toolbar="true"
      v-model="novaOcorrenciaContent">
    </input-rich-text>
    <div class="save-button-container" v-show="showSaveBtn">
      <Button
        data-testid="ocorrencia-save-btn"
        :label="saveLabel"
        icon="pi pi-save"
        severity="primary"
        size="small"
        @click="salvaNovaOcorrencia"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useOcorrencias } from '../../composables/useOcorrencias';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type OwnerId = string | number | null;

interface SaveOcorrenciaResponse {
  success?: boolean;
  dados?: unknown;
  [key: string]: unknown;
}

const props = withDefaults(defineProps<{
  owner: string;
  ownerId: OwnerId;
  saveLabel?: string;
  elemId?: string;
  showSaveBtn?: boolean;
  modelValue?: string;
}>(), {
  saveLabel: 'Salvar',
  elemId: 'inputNovaOcorrencia',
  showSaveBtn: false,
  modelValue: ''
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'input': [value: string];
}>();

const { saveOcorrencia } = useOcorrencias();

const novaOcorrenciaContent = ref<string>('');

const salvaNovaOcorrencia = async (): Promise<void> => {
  log('OccurrencesForm', 'salvaNovaOcorrencia', 'Starting save', {
    owner: props.owner,
    ownerId: props.ownerId,
    content: novaOcorrenciaContent.value
  });

  if (!props.owner || !props.ownerId) {
    log('OccurrencesForm', 'salvaNovaOcorrencia', 'Missing required props', { owner: props.owner, ownerId: props.ownerId });
    return;
  }

  if (!novaOcorrenciaContent.value || novaOcorrenciaContent.value.trim() === '') {
    log('OccurrencesForm', 'salvaNovaOcorrencia', 'Content is empty');
    return;
  }

  const payload = {
    owner: props.owner,
    ownerID: Number(props.ownerId),
    content: novaOcorrenciaContent.value
  };

  try {
    const response = await saveOcorrencia(payload) as SaveOcorrenciaResponse;
    log('OccurrencesForm', 'salvaNovaOcorrencia', 'Response received', response);

    novaOcorrenciaContent.value = '';
    eventBus.emit('ocorrencia.saved', response);
    log('OccurrencesForm', 'salvaNovaOcorrencia', 'Occurrence saved successfully', response);
  } catch (error) {
    // The user must SEE a refusal: the modal stays open and the text stays in
    // the editor, so they can retry or copy it out.
    log('OccurrencesForm', 'salvaNovaOcorrencia', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message);
  }
};

const resetForm = (): void => {
  novaOcorrenciaContent.value = '';
};

watch(() => props.modelValue, (newVal) => {
  novaOcorrenciaContent.value = newVal || '';
}, { immediate: true });

watch(novaOcorrenciaContent, (newVal) => {
  emit('update:modelValue', newVal);
  emit('input', newVal);
});

defineExpose({
  salvaNovaOcorrencia,
  resetForm
});
</script>

<style scoped>
/* Form-specific styles */
.save-button-container {
  text-align: right;
  margin-top: 10px;
}
</style>
